import {useCallback, ReactNode} from 'react';

import {defineMessages, FormattedMessage, MessageDescriptor} from 'react-intl';

import {Icon, Action, Tooltip, createView} from '@sail/ui';
import {cancel} from '@sail/icons/react/Icon';

import {useAnalytics} from '../AnalyticsProvider';
import {AssistantHeader} from './AssistantHeader';
import {
  AssistantMinimizeToggleButton,
  assistantHeaderButtonStyles,
} from './AssistantMinimizeToggleButton';

const messages = defineMessages({
  closeTooltip: {
    id: 'conversations_ui.header.closeTooltip',
    description: 'Label for the close botton',
    defaultMessage: 'Close',
  },
});

type Props = {
  title?: MessageDescriptor | ReactNode;
  height?: number | string;
  badge?: ReactNode;
  onClose: () => void;
  onMinimize?: () => void;
  onOpen?: () => void;
  visibility?: 'open' | 'minimized' | 'closed';
  headerActions?: ReactNode;
  subHeader?: ReactNode;
  hideSparkle?: boolean;
};

export const AssistantWidgetHeader = createView(
  ({
    title,
    height = 52,
    badge,
    visibility = 'open',
    onMinimize,
    onOpen,
    onClose,
    headerActions,
    subHeader,
    hideSparkle = false,
    ...props
  }: Props) => {
    const analytics = useAnalytics();

    const minimized = visibility === 'minimized';

    const handleMinimize = useCallback(() => {
      if (minimized) {
        onOpen?.();
      } else {
        onMinimize?.();
      }
      analytics.action('stripe_assistant_header_minimize_click');
    }, [minimized, onOpen, onMinimize, analytics]);

    const handleClose = useCallback(() => {
      onClose();
      analytics.action('stripe_assistant_header_close_click');
    }, [analytics, onClose]);

    const allHeaderActions = (
      <>
        {headerActions}
        <AssistantMinimizeToggleButton
          minimized={minimized}
          onPress={handleMinimize}
        />

        <Tooltip
          type="label"
          delay={600}
          placement={minimized ? 'top' : 'bottom'}
          trigger={
            <Action
              data-testid="conversations-widget-header-close-button"
              onPress={handleClose}
              uses={[assistantHeaderButtonStyles]}
            >
              <Icon icon={cancel} size="xsmall" />
            </Action>
          }
        >
          <FormattedMessage {...messages.closeTooltip} />
        </Tooltip>
      </>
    );

    return (
      <AssistantHeader
        title={title}
        height={height}
        badge={badge}
        headerActions={allHeaderActions}
        subHeader={subHeader}
        hideSparkle={hideSparkle}
        {...props}
      />
    );
  },
);
